import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Assistant } from '@/application/assistants/assistantsTypes';
import type { Role } from '@/application/permissions/permissionsTypes';
import { actionClass } from '@/presentation/components/common/actionButtons';
import { ModalPortal } from '@/presentation/components/common/ModalPortal';
import { useKeyEsc } from '@/presentation/hooks/useKeyEsc';

/** Reserva el ancho del label más largo para que Activar/Deshabilitar no mueva la columna. */
export function DualWidthLabel({ labels, current }: { labels: readonly string[]; current: string }) {
  return (
    <span className="inline-grid justify-items-center">
      {labels.map((label) => (
        <span key={label} className="invisible col-start-1 row-start-1 whitespace-nowrap" aria-hidden>
          {label}
        </span>
      ))}
      <span className="col-start-1 row-start-1 whitespace-nowrap">{current}</span>
    </span>
  );
}

export function AssistantStatusBadge({ status }: { status: Assistant['status'] }) {
  const { t } = useTranslation();
  const isActive = status === 'active';
  return (
    <span
      className={`inline-flex w-fit whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
        isActive
          ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
          : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
      }`}
    >
      {t(`assistants.status.${status}`)}
    </span>
  );
}

export function AssistantActions({
  assistant,
  layout,
  onEdit,
  onRecoverPassword,
  onResetTwoFactor,
  onToggleStatus,
  onDelete,
}: {
  assistant: Assistant;
  layout: 'compact' | 'mobile';
  onEdit: (assistant: Assistant) => void;
  onRecoverPassword: (assistant: Assistant) => void;
  onResetTwoFactor: (assistant: Assistant) => void;
  onToggleStatus: (assistant: Assistant) => Promise<void>;
  onDelete: (assistant: Assistant) => void;
}) {
  if (layout === 'mobile') {
    return (
      <MobileAssistantActions
        assistant={assistant}
        onEdit={onEdit}
        onRecoverPassword={onRecoverPassword}
        onResetTwoFactor={onResetTwoFactor}
        onToggleStatus={onToggleStatus}
        onDelete={onDelete}
      />
    );
  }

  return (
    <CompactAssistantActions
      assistant={assistant}
      onEdit={onEdit}
      onRecoverPassword={onRecoverPassword}
      onResetTwoFactor={onResetTwoFactor}
      onToggleStatus={onToggleStatus}
      onDelete={onDelete}
    />
  );
}

function CompactAssistantActions({
  assistant,
  onEdit,
  onRecoverPassword,
  onResetTwoFactor,
  onToggleStatus,
  onDelete,
}: {
  assistant: Assistant;
  onEdit: (assistant: Assistant) => void;
  onRecoverPassword: (assistant: Assistant) => void;
  onResetTwoFactor: (assistant: Assistant) => void;
  onToggleStatus: (assistant: Assistant) => Promise<void>;
  onDelete: (assistant: Assistant) => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const isActive = assistant.status === 'active';
  const enableLabel = t('assistants.actions.enable');
  const disableLabel = t('assistants.actions.disable');

  const toggleStatus = async () => {
    setBusy(true);
    try {
      await onToggleStatus(assistant);
    } catch {
      // El error ya se muestra a nivel de página.
    } finally {
      setBusy(false);
    }
  };

  const primary = actionClass('primary');
  const secondary = actionClass('secondary');
  const danger = actionClass('danger');

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button type="button" onClick={() => onEdit(assistant)} disabled={busy} className={primary}>
        {t('assistants.actions.edit')}
      </button>
      <button
        type="button"
        onClick={() => onRecoverPassword(assistant)}
        disabled={busy}
        aria-label={t('assistants.actions.recoverPassword')}
        className={secondary}
      >
        {t('assistants.actions.recoverPasswordShort')}
      </button>
      <button
        type="button"
        onClick={() => onResetTwoFactor(assistant)}
        disabled={busy}
        aria-label={t('assistants.actions.resetTwoFactor')}
        className={secondary}
      >
        {t('assistants.actions.resetTwoFactorShort')}
      </button>
      <button type="button" onClick={() => void toggleStatus()} disabled={busy} className={secondary}>
        <DualWidthLabel labels={[enableLabel, disableLabel]} current={isActive ? disableLabel : enableLabel} />
      </button>
      <button
        type="button"
        onClick={() => onDelete(assistant)}
        disabled={busy || isActive}
        title={isActive ? t('assistants.delete.mustDisable') : undefined}
        className={danger}
      >
        {t('assistants.actions.delete')}
      </button>
    </div>
  );
}

function MobileAssistantActions({
  assistant,
  onEdit,
  onRecoverPassword,
  onResetTwoFactor,
  onToggleStatus,
  onDelete,
}: {
  assistant: Assistant;
  onEdit: (assistant: Assistant) => void;
  onRecoverPassword: (assistant: Assistant) => void;
  onResetTwoFactor: (assistant: Assistant) => void;
  onToggleStatus: (assistant: Assistant) => Promise<void>;
  onDelete: (assistant: Assistant) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isActive = assistant.status === 'active';
  const enableLabel = t('assistants.actions.enable');
  const disableLabel = t('assistants.actions.disable');

  const close = () => setOpen(false);
  useKeyEsc(close, open);

  const toggleStatus = async () => {
    setBusy(true);
    close();
    try {
      await onToggleStatus(assistant);
    } catch {
      // El error ya se muestra a nivel de página.
    } finally {
      setBusy(false);
    }
  };

  const rowClass =
    'flex min-h-12 w-full items-center justify-center px-4 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';

  return (
    <>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onEdit(assistant)}
          disabled={busy}
          className="panel-touch flex-1 rounded-xl bg-teal-600 px-3 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50"
        >
          {t('assistants.actions.edit')}
        </button>
        <button
          type="button"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-label={t('assistants.actions.moreAria', { name: assistant.nombre })}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
          disabled={busy}
          className="panel-touch flex-1 rounded-xl border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          {t('assistants.actions.more')}
        </button>
      </div>

      {open && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          data-testid="assistant-actions-backdrop"
          onClick={(event) => {
            event.stopPropagation();
            close();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={`assistant-actions-title-${assistant.id}`}
            data-testid="assistant-actions-sheet"
            className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <h2
                id={`assistant-actions-title-${assistant.id}`}
                className="truncate text-sm font-semibold text-gray-900 dark:text-white"
              >
                {assistant.nombre}
              </h2>
              <p className="truncate text-xs text-gray-500 dark:text-gray-400">{assistant.email}</p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              <button
                type="button"
                onClick={() => {
                  close();
                  onRecoverPassword(assistant);
                }}
                className={`${rowClass} text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-950/40`}
              >
                {t('assistants.actions.recoverPassword')}
              </button>
              <button
                type="button"
                onClick={() => {
                  close();
                  onResetTwoFactor(assistant);
                }}
                className={`${rowClass} text-teal-700 hover:bg-teal-50 dark:text-teal-300 dark:hover:bg-teal-950/40`}
              >
                {t('assistants.actions.resetTwoFactor')}
              </button>
              <button
                type="button"
                onClick={() => void toggleStatus()}
                className={`${rowClass} text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700/60`}
              >
                {isActive ? disableLabel : enableLabel}
              </button>
              <button
                type="button"
                onClick={() => {
                  close();
                  onDelete(assistant);
                }}
                disabled={isActive}
                title={isActive ? t('assistants.delete.mustDisable') : undefined}
                className={`${rowClass} text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40`}
              >
                {t('assistants.actions.delete')}
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </>
  );
}

export function assistantRoleName(assistant: Assistant, roles: Role[], fallback: string): string {
  return roles.find((role) => role.id === assistant.panelRoleId)?.name ?? fallback;
}

export function assistantInitials(nombre: string): string {
  const parts = nombre.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}
